/**
 * Account identity and avatar dropdown menu — Stage 6c (Single-View DOM & Navigation Unification).
 *
 * Extracts and standardizes user identity formatting, dropdown positioning, and
 * the safe logout workflow across views (index.html, paper.html, live.html).
 */

export function extractInitials(name?: string | null, email?: string | null): string {
  const n = (name || "").trim();
  if (n) {
    const p = n.split(/\s+/);
    return ((p[0][0] || "") + (p.length > 1 ? p[p.length - 1][0] || "" : "")).toUpperCase();
  }
  const em = (email || "").trim();
  return em ? em[0].toUpperCase() : "?";
}

export function capitalize(s?: string | null): string {
  const str = String(s || "");
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

export function formatAccountSubline(
  user?: { id?: number | string | null } | null,
  subscription?: { plan?: string | null; status?: string | null } | null
): string {
  const bits: string[] = [];
  if (subscription?.plan) bits.push(capitalize(subscription.plan));
  if (subscription?.status) bits.push(capitalize(subscription.status));
  if (user?.id != null) bits.push(`ID ${user.id}`);
  return bits.join(" · ");
}

export function formatDisplayName(
  user?: { name?: string | null; email?: string | null } | null
): string {
  return (user?.name && user.name.trim()) || user?.email || "Account";
}

export interface BuildAccountMenuOptions {
  showLayouts?: boolean;
  showPreferences?: boolean;
}

export function buildAccountMenuHtml(
  options: BuildAccountMenuOptions = { showLayouts: true, showPreferences: true }
): string {
  const layoutsItem = options.showLayouts
    ? `<button class="am-item" id="am-layouts"><span class="am-ic">▤</span>Saved layouts</button>`
    : "";
  const prefsItem = options.showPreferences
    ? `<button class="am-item" id="am-preferences"><span class="am-ic">⚙</span>Preferences</button>`
    : "";
  const sep = layoutsItem || prefsItem ? `<div class="am-sep"></div>` : "";

  return `
<div class="am-header">
  <div id="am-avatar-lg" class="am-avatar-lg">?</div>
  <div class="am-identity">
    <div id="am-name" class="am-name">Account</div>
    <div id="am-sub" class="am-sub"></div>
  </div>
</div>
${sep}
${layoutsItem}
${prefsItem}
<div class="am-sep"></div>
<button class="am-item am-danger" id="am-logout"><span class="am-ic">⎋</span>Log out</button>
`.trim();
}

export interface LogoutOptions {
  logoutUrl?: string;
  tokenKey?: string;
  redirectUrl?: string;
  authHeaders?: () => Record<string, string>;
  fetchFn?: typeof fetch;
  storage?: { removeItem(key: string): void };
  redirectFn?: (url: string) => void;
}

export async function performLogout(options: LogoutOptions = {}): Promise<void> {
  const logoutUrl = options.logoutUrl || "/api/auth/logout";
  const tokenKey = options.tokenKey || "nikhilchart_auth_token";
  const redirectUrl = options.redirectUrl !== undefined ? options.redirectUrl : "/";
  const headers = options.authHeaders ? options.authHeaders() : {};
  const fetcher = options.fetchFn || (typeof fetch !== "undefined" ? fetch : undefined);

  if (fetcher) {
    try {
      await fetcher(logoutUrl, { method: "POST", headers });
    } catch (_) {}
  }

  const storage =
    options.storage || (typeof localStorage !== "undefined" ? localStorage : undefined);
  if (storage) {
    try {
      storage.removeItem(tokenKey);
    } catch (_) {}
  }

  if (options.redirectFn) {
    options.redirectFn(redirectUrl);
  } else if (typeof window !== "undefined" && redirectUrl) {
    window.location.href = redirectUrl;
  }
}

export interface MinimalElement {
  style: Record<string, string>;
  textContent?: string | null;
  getBoundingClientRect?(): { bottom: number; right: number };
  addEventListener(event: string, handler: (e: any) => void): void;
  removeEventListener(event: string, handler: (e: any) => void): void;
  contains?(target: any): boolean;
  querySelector?(selector: string): any;
}

export interface MinimalDocument {
  addEventListener(event: string, handler: (e: any) => void): void;
  removeEventListener(event: string, handler: (e: any) => void): void;
}

export interface MinimalWindow {
  innerWidth: number;
}

export interface AccountMenuWiringOptions {
  avatarEl: MinimalElement;
  menuEl: MinimalElement;
  doc?: MinimalDocument;
  win?: MinimalWindow;
  authMeUrl?: string;
  authHeaders?: () => Record<string, string>;
  fetchFn?: typeof fetch;
  onLayouts?: () => void;
  onPreferences?: () => void;
  onLogout?: () => Promise<void> | void;
  onAuthFailure?: (resp: Response) => void;
}

export function setupAccountMenu(options: AccountMenuWiringOptions): {
  close: () => void;
  open: () => void;
  refreshIdentity: () => Promise<void>;
  destroy: () => void;
} {
  const { avatarEl, menuEl } = options;
  const doc = options.doc || (typeof document !== "undefined" ? document : null);
  const win = options.win || (typeof window !== "undefined" ? window : null);

  function close() {
    menuEl.style.display = "none";
  }

  function open() {
    if (avatarEl.getBoundingClientRect && win) {
      const r = avatarEl.getBoundingClientRect();
      menuEl.style.top = r.bottom + 6 + "px";
      menuEl.style.right = Math.max(8, Math.round(win.innerWidth - r.right)) + "px";
    }
    menuEl.style.display = "block";
  }

  function handleAvatarClick(e: { stopPropagation?: () => void }) {
    if (e.stopPropagation) e.stopPropagation();
    if (menuEl.style.display === "block") {
      close();
    } else {
      open();
    }
  }

  function handleMenuClick(e: { stopPropagation?: () => void }) {
    if (e.stopPropagation) e.stopPropagation();
  }

  function handleDocClick(e: { target: any }) {
    if (
      menuEl.style.display === "block" &&
      menuEl.contains &&
      !menuEl.contains(e.target) &&
      e.target !== avatarEl
    ) {
      close();
    }
  }

  function handleDocKeydown(e: { key: string }) {
    if (e.key === "Escape" && menuEl.style.display === "block") {
      close();
    }
  }

  avatarEl.addEventListener("click", handleAvatarClick);
  menuEl.addEventListener("click", handleMenuClick);
  if (doc) {
    doc.addEventListener("click", handleDocClick);
    doc.addEventListener("keydown", handleDocKeydown);
  }

  if (menuEl.querySelector) {
    const layoutsBtn = menuEl.querySelector("#am-layouts");
    if (layoutsBtn && options.onLayouts) {
      layoutsBtn.addEventListener("click", () => {
        close();
        options.onLayouts!();
      });
    }

    const prefsBtn = menuEl.querySelector("#am-preferences");
    if (prefsBtn && options.onPreferences) {
      prefsBtn.addEventListener("click", () => {
        close();
        options.onPreferences!();
      });
    }

    const logoutBtn = menuEl.querySelector("#am-logout");
    if (logoutBtn) {
      logoutBtn.addEventListener("click", async () => {
        close();
        if (options.onLogout) {
          await options.onLogout();
        } else {
          await performLogout({
            authHeaders: options.authHeaders,
            fetchFn: options.fetchFn,
          });
        }
      });
    }
  }

  async function refreshIdentity(): Promise<void> {
    const authMeUrl = options.authMeUrl || "/api/auth/me";
    const headers = options.authHeaders ? options.authHeaders() : {};
    const fetcher = options.fetchFn || (typeof fetch !== "undefined" ? fetch : undefined);
    if (!fetcher) return;

    try {
      const resp = await fetcher(authMeUrl, { headers });
      if (!resp.ok) {
        if (options.onAuthFailure) options.onAuthFailure(resp);
        return;
      }
      const data = await resp.json();
      const u = data.user || {};
      const ini = extractInitials(u.name, u.email);

      avatarEl.textContent = ini;
      if (menuEl.querySelector) {
        const avatarLg = menuEl.querySelector("#am-avatar-lg");
        if (avatarLg) avatarLg.textContent = ini;

        const nameEl = menuEl.querySelector("#am-name");
        if (nameEl) nameEl.textContent = formatDisplayName(u);

        const subEl = menuEl.querySelector("#am-sub");
        if (subEl) subEl.textContent = formatAccountSubline(u, data.subscription);
      }
    } catch (_) {
      // Degrade gracefully with fallback initials, never throw
    }
  }

  void refreshIdentity();

  function destroy() {
    avatarEl.removeEventListener("click", handleAvatarClick);
    menuEl.removeEventListener("click", handleMenuClick);
    if (doc) {
      doc.removeEventListener("click", handleDocClick);
      doc.removeEventListener("keydown", handleDocKeydown);
    }
  }

  return {
    close,
    open,
    refreshIdentity,
    destroy,
  };
}
